import React from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import CloseIcon from "@mui/icons-material/Close";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import PriorityHighOutlinedIcon from "@mui/icons-material/PriorityHighOutlined";

function asList(value) {
  return Array.isArray(value) ? value : [];
}

function GuideBlock({ title, children, defaultExpanded = false }) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      sx={{
        bgcolor: "transparent",
        "&:before": { display: "none" },
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ px: 0, minHeight: 40 }}
      >
        <Typography fontWeight={700} variant="body2">
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pt: 0, pb: 1.25 }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

function BulletList({ items, icon: Icon, color }) {
  return (
    <Stack spacing={0.65}>
      {items.map((item) => (
        <Stack key={item} direction="row" spacing={1} alignItems="flex-start">
          {Icon ? (
            <Icon fontSize="small" sx={{ color, mt: 0.15, flexShrink: 0 }} />
          ) : (
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: color || "primary.main",
                mt: 0.85,
                flexShrink: 0,
              }}
            />
          )}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.8125rem" }}
          >
            {item}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

/**
 * @param {{ open: boolean, onClose: () => void, isMiguel?: boolean, variant?: 'sidebar' | 'stack' }} props
 */
export default function SkillFichaHelpPanel({
  open,
  onClose,
  isMiguel,
  variant = "sidebar",
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const isSidebar = variant === "sidebar";
  const flow = asList(t("skillFicha.help.flow", { returnObjects: true }));
  const why = asList(t("skillFicha.help.why", { returnObjects: true }));
  const sections = asList(
    t("skillFicha.help.sections", { returnObjects: true }),
  );
  const chips = asList(t("skillFicha.help.chips", { returnObjects: true }));
  const fails = asList(t("skillFicha.help.fails", { returnObjects: true }));
  const before = asList(t("skillFicha.help.before", { returnObjects: true }));

  if (!open) return null;

  const content = (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          bgcolor: alpha(theme.palette.background.paper, 0.92),
          backdropFilter: "blur(8px)",
          pb: 1,
          mb: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: "primary.main",
            }}
          >
            <HelpOutlineOutlinedIcon fontSize="small" />
          </Box>
          <Box>
            <Typography fontWeight={800} variant="subtitle1">
              {t("skillFicha.help.title")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("skillFicha.help.subtitle")}
            </Typography>
          </Box>
        </Stack>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label={t("skillFicha.help.close")}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Typography variant="body2" sx={{ mb: 1.5, fontSize: "0.8125rem" }}>
        <Trans i18nKey="skillFicha.help.lead" components={{ b: <b /> }} />
      </Typography>

      <Stack
        direction="row"
        spacing={0.75}
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 1.5 }}
      >
        {chips.map((label, i) => (
          <Chip
            key={label}
            size="small"
            label={label}
            color={i === 0 ? "primary" : "default"}
            variant="outlined"
          />
        ))}
      </Stack>

      <GuideBlock title={t("skillFicha.help.flowTitle")} defaultExpanded>
        <Stack spacing={1.1}>
          {flow.map((step, i) => (
            <Stack
              key={step.title}
              direction="row"
              spacing={1.1}
              alignItems="flex-start"
            >
              <Box
                sx={{
                  minWidth: 22,
                  height: 22,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  fontSize: 11,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mt: 0.15,
                }}
              >
                {i + 1}
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{ fontSize: "0.8125rem" }}
                >
                  {step.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.78rem" }}
                >
                  {step.text}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      </GuideBlock>

      {why.map((block) => (
        <GuideBlock
          key={block.title}
          title={block.title}
          defaultExpanded={false}
        >
          <BulletList items={block.body} />
        </GuideBlock>
      ))}

      <GuideBlock title={t("skillFicha.help.sectionsTitle")} defaultExpanded>
        <Stack spacing={1.25}>
          {sections.map((sec) => (
            <Box
              key={sec.name}
              sx={{
                p: 1.25,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.background.default, 0.65),
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography fontWeight={700} variant="body2" sx={{ mb: 0.75 }}>
                {sec.name}
              </Typography>
              <Typography
                variant="caption"
                fontWeight={700}
                color="success.main"
                sx={{ display: "block", mb: 0.35 }}
              >
                {t("skillFicha.help.must")}
              </Typography>
              <BulletList
                items={sec.must}
                icon={CheckCircleOutlineIcon}
                color={theme.palette.success.main}
              />
              <Typography
                variant="caption"
                fontWeight={700}
                color="error.main"
                sx={{ display: "block", mt: 0.75, mb: 0.35 }}
              >
                {t("skillFicha.help.avoid")}
              </Typography>
              <BulletList
                items={sec.avoid}
                icon={CancelOutlinedIcon}
                color={theme.palette.error.main}
              />
            </Box>
          ))}
        </Stack>
      </GuideBlock>

      <GuideBlock title={t("skillFicha.help.failsTitle")}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          sx={{ mb: 1 }}
        >
          <PriorityHighOutlinedIcon
            color="warning"
            fontSize="small"
            sx={{ mt: 0.15 }}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.78rem" }}
          >
            {t("skillFicha.help.failsLead")}
          </Typography>
        </Stack>
        <BulletList items={fails} color={theme.palette.warning.main} />
      </GuideBlock>

      <GuideBlock title={t("skillFicha.help.beforeTitle")} defaultExpanded>
        <BulletList
          items={before}
          icon={CheckCircleOutlineIcon}
          color={theme.palette.primary.main}
        />
      </GuideBlock>

      <GuideBlock title={t("skillFicha.help.exampleTitle")}>
        <Stack spacing={0.75}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.78rem" }}
          >
            <b style={{ color: theme.palette.success.main }}>
              {t("skillFicha.help.exampleGoodLabel")}
            </b>{" "}
            {t("skillFicha.help.exampleGood")}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.78rem" }}
          >
            <b style={{ color: theme.palette.error.main }}>
              {t("skillFicha.help.exampleBadLabel")}
            </b>{" "}
            {t("skillFicha.help.exampleBad")}
          </Typography>
        </Stack>
      </GuideBlock>

      {isMiguel && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          sx={{
            mt: 1.25,
            pt: 1.25,
            borderTop: "1px dashed",
            borderColor: "divider",
          }}
        >
          <LightbulbOutlinedIcon
            fontSize="small"
            color="warning"
            sx={{ mt: 0.15 }}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.78rem" }}
          >
            <Trans i18nKey="skillFicha.help.miguel" components={{ b: <b /> }} />
          </Typography>
        </Stack>
      )}
    </>
  );

  if (isSidebar) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: { xs: 0, lg: 3 },
          border: "1px solid",
          borderColor: alpha(theme.palette.primary.main, 0.22),
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            p: { xs: 1.5, sm: 2 },
            "&::-webkit-scrollbar": { width: 8 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: alpha(theme.palette.primary.main, 0.25),
              borderRadius: 4,
            },
          }}
        >
          {content}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 3,
        border: "1px solid",
        borderColor: alpha(theme.palette.primary.main, 0.28),
        bgcolor: alpha(theme.palette.primary.main, 0.05),
        maxHeight: "55vh",
        overflow: "auto",
      }}
    >
      {content}
    </Box>
  );
}
